import { useContext, useState, useRef, useEffect, useCallback } from "react";
import "./DocumentUploadPage.css";
import { Button } from "primereact/button";
import { uploadDocumentChunksToClaim } from "../../services/packets";
import * as Sentry from "@sentry/browser";
import { Accordion, AccordionTab } from "primereact/accordion";
import { defaultDocumentTypes } from "../../services/constants";
import DocumentUploadList from "./DocumentUploadList";
import { SidebarContext } from "../CreateNewClaimModal/CreateNewClaimModal";

const DocumentUploadPage = ({
  packet,
  selectedOrders = [],
  existingOrders = [],
  skipRequiredDocs,
  titleType,
  xTitleSelected,
  stepperRef,
  uploadedDocs = [],
  setUploadedDocs = () => {},
  mode,
  revertMode
}) => {
  const {
    ownerSignedTitleImage,
    setOwnerSignedTitleImage,
    odometerInTitleImage,
    setOdometerInTitleImage,
    signedLRInTitleImage,
    setSignedLRInTitleImage
  } = useContext(SidebarContext);
  const [docsBeingUploaded, setDocsBeingUploaded] = useState([]);
  const [requiredSatisfied, setRequiredSatisfied] = useState(false);
  const [xTitleSatisfied, setXTitleSatisfied] = useState(false);
  const [accordionLock, setAccordionLock] = useState(false);
  const [documentTypes, setDocumentTypes] = useState(defaultDocumentTypes);
  const [errors, setErrors] = useState({});

  const toastRef = useRef(null);

  const validate = useCallback(() => {
    const e = {};
    if (!requiredSatisfied && !skipRequiredDocs) {
      e.required = "Required documents are not complete";
    }
    if (selectedOrders["xTitle"] && !xTitleSatisfied) {
      e.xTitle = "Title Transfer documents are not complete";
    }

    if (errors?.required !== e?.required || errors?.xTitle !== e?.xTitle) {
      setErrors(e);
    }
    return e;
  }, [
    requiredSatisfied,
    xTitleSatisfied,
    selectedOrders,
    skipRequiredDocs,
    errors?.required,
    errors?.xTitle
  ]);

  // Expose a function in the console to skip
  useEffect(() => {
    window.skip = () => stepperRef.current.nextCallback();
    window.reset = () => {
      setUploadedDocs([]);
    };
    return () => {
      delete window.skip; // Cleanup when unmounted
    };
  }, [stepperRef, setUploadedDocs]);

  // on mount, if uploadedDocs length is 0, fetch them again to make sure we have the most upto date list
  useEffect(() => {
    if (uploadedDocs.length === 0) {
      const packetDocuments = packet?.documents.map(doc => doc.type);
      const documents = packetDocuments
        ?.map(doc => {
          return defaultDocumentTypes.find(docType => docType.value === doc);
        })
        .filter(Boolean);
      if (uploadedDocs?.length !== documents?.length) {
        setUploadedDocs(documents);
      }
    }
  }, [packet, uploadedDocs, setUploadedDocs]);

  // check if required and xTitle documents are satisfied
  useEffect(() => {
    const xTitleCurrentlySatisfied = xTitleSatisfied;
    const skipRequiredSection =
      selectedOrders["xTitle"] && Object.keys(selectedOrders).length === 1;
    const uploadedDocLabels = uploadedDocs.map(doc => doc?.label);

    if (skipRequiredSection && !requiredSatisfied) {
      setRequiredSatisfied(true);
    } else {
      const requiredDocs = documentTypes.filter(
        docType => docType.types === "required"
      );
      const requiredSat = requiredDocs.every(doc =>
        uploadedDocLabels.includes(doc?.label)
      );

      if (requiredSat && requiredSat !== requiredSatisfied) {
        setRequiredSatisfied(requiredSat);
      }
    }

    const xTitleDocsA = documentTypes.filter(
      docType => docType.types === "xtitle-required-A"
    );
    // const xTitleDocsB = documentTypes.filter(
    //   docType => docType.types === "xtitle-required-B"
    // );
    // temporarily making B section impossible to satisfy, uncomment lines above to add back second requirement check
    const xTitleDocsB = [{ label: "Blocked" }];
    const xTitleSat =
      xTitleDocsA.every(doc => uploadedDocLabels.includes(doc?.label)) ||
      xTitleDocsB.every(doc => uploadedDocLabels.includes(doc?.label));
    if (xTitleCurrentlySatisfied !== xTitleSat) {
      setXTitleSatisfied(xTitleSat);
    }

    // if errors have been set, check again
    if (Object.keys(errors).length > 0) {
      validate();
    }
  }, [
    uploadedDocs,
    documentTypes,
    validate,
    errors,
    requiredSatisfied,
    xTitleSatisfied,
    selectedOrders,
    accordionLock,
    ownerSignedTitleImage,
    odometerInTitleImage,
    signedLRInTitleImage
  ]);

  // if title type is electronic, make lien release optional
  useEffect(() => {
    if (titleType === "ELECTRONIC") {
      const newDocumentTypes = documentTypes?.map(docType => {
        if (docType?.label === "Lien Release (Signed)") {
          return { ...docType, types: "optional" };
        }
        return docType;
      });
      if (
        documentTypes?.find(d => d?.label === "Lien Release (Signed)")
          ?.types !== "optional"
      ) {
        setDocumentTypes(newDocumentTypes);
      }
    }
  }, [titleType, documentTypes]);

  // handle conditionally making xtitle docs optional or required
  // based on checkbox modifiers
  useEffect(() => {
    let originalDocTypes = documentTypes;

    let poaDoc;
    let poaOptional = false;
    let poaRequiredA = false;

    let odometerDoc;
    let odometerOptional = false;
    let odometerRequiredA = false;

    let lienReleaseDoc;
    let lienReleaseOptional = false;
    let lienReleaseRequiredA = false;

    originalDocTypes.forEach(docType => {
      if (docType?.label === "Power of Attorney") {
        poaDoc = docType;
        if (docType?.types === "xtitle-optional") {
          poaOptional = true;
        }
        if (docType?.types === "xtitle-required-A") {
          poaRequiredA = true;
        }
      }
      if (docType?.label === "Odometer Disclosure") {
        odometerDoc = docType;
        if (docType?.types === "xtitle-optional") {
          odometerOptional = true;
        }
        if (docType?.types === "xtitle-required-A") {
          odometerRequiredA = true;
        }
      }
      if (docType?.label === "Lien Release (Signed)") {
        lienReleaseDoc = docType;
        if (docType?.types === "xtitle-optional") {
          lienReleaseOptional = true;
        }
        if (docType?.types === "xtitle-required-A") {
          lienReleaseRequiredA = true;
        }
      }
    });

    const addDoc = (doc, types) => {
      let newDocumentTypes = originalDocTypes;
      if (
        !newDocumentTypes.find(
          d => d?.label === doc?.label && d?.types === types
        )
      ) {
        newDocumentTypes.push({ ...doc, types });
        originalDocTypes = newDocumentTypes;
        setDocumentTypes(newDocumentTypes);
        setAccordionLock(true);
      }
    };

    const removeDoc = (doc, types) => {
      let newDocumentTypes = [];
      originalDocTypes.forEach(d => {
        if (d?.label !== doc?.label || d?.types !== types) {
          newDocumentTypes.push(d);
        } else {
        }
      });
      originalDocTypes = newDocumentTypes;
      setDocumentTypes(newDocumentTypes);
      setAccordionLock(true);
    };

    // We could get fancier than this, but the logic is so weird
    // that this is the most readable way to do it
    if (ownerSignedTitleImage) {
      if (poaRequiredA) {
        removeDoc(poaDoc, "xtitle-required-A");
        poaRequiredA = false;
      }
      if (!poaOptional) {
        addDoc(poaDoc, "xtitle-optional");
        poaOptional = true;
      }
    } else {
      if (poaOptional) {
        removeDoc(poaDoc, "xtitle-optional");
        poaOptional = false;
      }
      if (!poaRequiredA) {
        addDoc(poaDoc, "xtitle-required-A");
        poaRequiredA = true;
      }
    }

    if (odometerInTitleImage) {
      if (odometerRequiredA) {
        removeDoc(odometerDoc, "xtitle-required-A");
        odometerRequiredA = false;
      }
      if (!odometerOptional) {
        addDoc(odometerDoc, "xtitle-optional");
        odometerOptional = true;
      }
    } else {
      if (odometerOptional) {
        removeDoc(odometerDoc, "xtitle-optional");
        odometerOptional = false;
      }
      if (!odometerRequiredA) {
        addDoc(odometerDoc, "xtitle-required-A");
        odometerRequiredA = true;
      }
    }

    if (signedLRInTitleImage) {
      if (lienReleaseRequiredA) {
        removeDoc(lienReleaseDoc, "xtitle-required-A");
        lienReleaseRequiredA = false;
      }
      if (!lienReleaseOptional) {
        addDoc(lienReleaseDoc, "xtitle-optional");
        lienReleaseOptional = true;
      }
    } else {
      if (lienReleaseOptional) {
        removeDoc(lienReleaseDoc, "xtitle-optional");
        lienReleaseOptional = false;
      }
      if (!lienReleaseRequiredA) {
        addDoc(lienReleaseDoc, "xtitle-required-A");
        lienReleaseRequiredA = true;
      }
    }
  }, [
    documentTypes,
    uploadedDocs,
    ownerSignedTitleImage,
    odometerInTitleImage,
    signedLRInTitleImage
  ]);

  const handleDocuments = (
    type,
    claimsSpecialistReviewNeeded = true
  ) => async e => {
    const files = e.files;
    setDocsBeingUploaded(docs => [...docs, type]);
    let fileCheck = Object.keys(files);
    const actualType = documentTypes.filter(
      docType => docType.label === type
    )[0];
    if (fileCheck.length > 0) {
      try {
        await Promise.all(
          fileCheck.map(key => {
            return uploadDocumentChunksToClaim(
              packet.packetId,
              files[key],
              actualType.value,
              {},
              {
                claimsSpecialistReviewNeeded
              }
            ).then();
          })
        );
        setDocsBeingUploaded(docs => docs.filter(doc => doc !== type));
        const formattedDocs = documentTypes.filter(
          docType => docType.label === type
        );
        setUploadedDocs(docs => {
          if (docs?.length > 0) {
            return [...docs, formattedDocs[0]];
          }
          return [formattedDocs[0]];
        });
      } catch (ex) {
        Sentry.captureException(ex);
        setDocsBeingUploaded([]);
        toastRef.current.show({
          baseZIndex: 1000,
          severity: "error",
          summary: "Error Uploading Document",
          detail:
            "Document must be less than 50 MB and in one of the following formats: .pdf, .png, .jpg, .jpeg, .tiff"
        });
      }
      e.options.clear();
    }
  };

  const goBack = () => {
    stepperRef.current.prevCallback();
  };

  const submitDocuments = () => {
    const e = validate();
    if (Object.keys(e).length === 0) {
      stepperRef.current.nextCallback();
    }
  };

  return (
    <div className={"DocUploadContainer"}>
      <p style={{ textAlign: "center" }}>
        Currently supported file types: .pdf, .png, .jpg, .jpeg, .tiff You may
        re-upload files by clicking the relevant button again.
      </p>
      <Accordion multiple activeIndex={[0, 1]}>
        {!(
          xTitleSelected &&
          Object.keys({ ...selectedOrders, ...existingOrders }).length === 1
        ) && (
          <AccordionTab
            className="RequiredHeader"
            header={
              <span
                className={`SectionHeader ${errors.required ? " Error" : ""}`}
              >
                {`Required Documents ${requiredSatisfied ? "(Complete)" : ""}`}
              </span>
            }
          >
            <div className={"RequiredDocs"}>
              <DocumentUploadList
                documents={documentTypes.filter(
                  docType => docType.types === "required"
                )}
                uploadedDocs={uploadedDocs}
                toastRef={toastRef}
                handleDocuments={handleDocuments}
                docsBeingUploaded={docsBeingUploaded}
              />
            </div>
          </AccordionTab>
        )}

        {xTitleSelected && (
          <AccordionTab
            className="xTitleHeader"
            header={
              <span className={`SectionHeader ${errors.xTitle ? "Error" : ""}`}>
                {`Express Title Documents ${
                  xTitleSatisfied ? "(Complete)" : ""
                }`}
              </span>
            }
          >
            <div className={"xTitleDocs"}>
              {/* <div className={`xTitleMessage ${xtitleMessageClass}`}>
                Instructions: Either a signed Title Image or a Motor Vehicle
                Report is required for a title transfer. Depending on which is
                selected, different supporting documents are also required.
                Please complete either the Title or MVR section. The download
                button next to some documents will provide a template for you to
                use.
              </div> */}
              <div
                className={`OwnerSigWarning ${
                  !ownerSignedTitleImage ? "show" : ""
                }`}
              >
                * If the Title was signed on behalf of the Owner by a third
                party, you must also upload a Power of Attorney signed by the
                Owner. If the odometer is not included in the owner-signed Title
                Image or the owner-signed PoA, the title will have a "Not Actual
                Odometer" brand
              </div>
              <div
                className={`OdometerWarning ${
                  !odometerInTitleImage ? "show" : ""
                }`}
              >
                * If the Odometer Information is not included in the Title Image
                you must also upload an Odometer Disclosure. If the owner did
                not personally sign the odometer disclosure, the title will have
                a "Not Actual Odometer" brand
              </div>
              <div
                className={`LRWarning ${!signedLRInTitleImage ? "show" : ""}`}
              >
                * If the signed Lien Release is not included in the Title Image
                you must also upload a signed Lien Release
              </div>
              {/*<motion.div*/}
              {/*  layout*/}
              {/*  transition={{ type: "spring", stiffness: 300, damping: 30 }}*/}
              {/*>*/}
              <DocumentUploadList
                documents={documentTypes.filter(
                  docType => docType.types === "xtitle-required-A"
                )}
                setOwnerSignedTitleImage={setOwnerSignedTitleImage}
                setOdometerInTitleImage={setOdometerInTitleImage}
                setSignedLRInTitleImage={setSignedLRInTitleImage}
                // groupName="Title"
                uploadedDocs={uploadedDocs}
                toastRef={toastRef}
                handleDocuments={handleDocuments}
                docsBeingUploaded={docsBeingUploaded}
              />
              {/*</motion.div>*/}

              {/* <div className="OrDivider"> - OR -</div> */}

              {/*<motion.div*/}
              {/*  layout*/}
              {/*  transition={{ type: "spring", stiffness: 300, damping: 30 }}*/}
              {/*>*/}
              {/* <DocumentUploadList
                documents={documentTypes.filter(
                  docType => docType.types === "xtitle-required-B"
                )}
                groupName="MVR"
                uploadedDocs={uploadedDocs}
                toastRef={toastRef}
                handleDocuments={handleDocuments}
                docsBeingUploaded={docsBeingUploaded}
              /> */}
              {/*</motion.div>*/}
            </div>
          </AccordionTab>
        )}

        <AccordionTab
          header={<span className="SectionHeader">Optional Documents</span>}
        >
          {/*<motion.div*/}
          {/*  layout*/}
          {/*  transition={{ type: "spring", stiffness: 300, damping: 30 }}*/}
          {/*>*/}
          <DocumentUploadList
            documents={documentTypes.filter(docType => {
              if (selectedOrders["xTitle"]) {
                return (
                  docType.types === "optional" ||
                  docType.types === "xtitle-optional"
                );
              }
              return docType.types === "optional";
            })}
            uploadedDocs={uploadedDocs}
            toastRef={toastRef}
            handleDocuments={handleDocuments}
            docsBeingUploaded={docsBeingUploaded}
          />
          {/*</motion.div>*/}
        </AccordionTab>
      </Accordion>
      <div className={"Sep"}></div>
      <div className={"Error"}>
        {errors.required && <p>{errors.required}</p>}
        {errors.xTitle && <p>{errors.xTitle}</p>}
      </div>
      {mode === "documents" ? (
        <div className={"ClaimNavButtons"}>
          <Button
            label={"Close"}
            onClick={() => revertMode()}
            type={"button"}
            className={"Button"}
            icon={"pi pi-arrow-left"}
            iconPos={"left"}
          />
        </div>
      ) : (
        <div className={"ClaimNavButtons"}>
          <Button
            label={"Go Back"}
            onClick={goBack}
            type={"button"}
            className={"NextButton"}
            icon={"pi pi-arrow-left"}
            iconPos={"left"}
          />
          <Button
            label={"Claim Information"}
            onClick={() => submitDocuments()}
            type={"submit"}
            className={"NextButton"}
            icon={"pi pi-arrow-right"}
            iconPos={"right"}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentUploadPage;
